import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Joyride, { ACTIONS, EVENTS, STATUS } from "react-joyride";
import "../style/component/sidebar.css";

const SPOTLIGHT_PADDING = 8;
const BOX_GREEN = "#3e7a5a";

const getSpotlightRect = (targetSelector) => {
  const element = document.querySelector(targetSelector);

  if (!element) {
    return null;
  }

  const rect = element.getBoundingClientRect();

  return {
    top: Math.round(Math.max(0, rect.top - SPOTLIGHT_PADDING)),
    left: Math.round(Math.max(0, rect.left - SPOTLIGHT_PADDING)),
    width: Math.round(rect.width + SPOTLIGHT_PADDING * 2),
    height: Math.round(rect.height + SPOTLIGHT_PADDING * 2),
  };
};

const TourTooltip = ({
  index,
  size,
  step,
  tooltipProps,
  primaryProps,
  isLastStep,
  onRequestSkip,
  isTourLocked,
}) => {
  const { children: _primaryLabel, onClick: onNext, ...nextProps } =
    primaryProps || {};

  return (
    <div className="rb-tour-tooltip" {...tooltipProps}>
      <span className="rb-tour-tooltip-progress">
        {index + 1} of {size}
      </span>
      {step.title ? (
        <h3 className="rb-tour-tooltip-title">{step.title}</h3>
      ) : null}
      <div className="rb-tour-tooltip-text">{step.content}</div>
      <div className="rb-tour-tooltip-actions">
        <button
          type="button"
          className="rb-tour-tooltip-skip"
          onClick={onRequestSkip}
        >
          Skip
        </button>
        <button
          type="button"
          className="rb-tour-tooltip-next"
          {...nextProps}
          onClick={(event) => {
            if (isTourLocked?.()) {
              event.preventDefault();
              event.stopPropagation();
              return;
            }
            onNext?.(event);
          }}
        >
          {isLastStep ? "Done" : "Next"}
        </button>
      </div>
    </div>
  );
};

const SkipOnboardingModal = ({ isOpen, onContinue, onSkip }) => {
  if (!isOpen) {
    return null;
  }

  return createPortal(
    <div
      className="rb-tour-skip-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="rb-tour-skip-title"
      onMouseDown={(event) => event.stopPropagation()}
      onClick={(event) => event.stopPropagation()}
    >
      <div
        className="rb-tour-skip-box"
        onClick={(event) => event.stopPropagation()}
      >
        <h2 id="rb-tour-skip-title">Skip onboarding?</h2>
        <p>
          You can explore Rally Bulls on your own, but you’ll miss the guided
          tour of the platform’s key features.
        </p>
        <div className="rb-tour-skip-actions">
          <button
            type="button"
            className="rb-tour-skip-continue"
            onClick={onContinue}
          >
            Continue Onboarding
          </button>
          <button
            type="button"
            className="rb-tour-skip-anyway"
            onClick={onSkip}
          >
            Skip Anyway
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

const TourOverlay = ({ rect, instant }) => {
  if (!rect) {
    return null;
  }

  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const panelClass = instant
    ? "rb-tour-panel rb-tour-panel--instant"
    : "rb-tour-panel";

  return (
    <div className="rb-tour-layer">
      <div
        className={panelClass}
        style={{ top: 0, left: 0, width: "100%", height: rect.top }}
      />
      <div
        className={panelClass}
        style={{
          top: rect.top,
          left: 0,
          width: rect.left,
          height: rect.height,
        }}
      />
      <div
        className={panelClass}
        style={{
          top: rect.top,
          left: rect.left + rect.width,
          width: Math.max(0, viewportWidth - rect.left - rect.width),
          height: rect.height,
        }}
      />
      <div
        className={panelClass}
        style={{
          top: rect.top + rect.height,
          left: 0,
          width: "100%",
          height: Math.max(0, viewportHeight - rect.top - rect.height),
        }}
      />
      <div
        className={`rb-tour-spotlight${instant ? " rb-tour-spotlight--instant" : ""}`}
        style={{
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height,
        }}
      />
    </div>
  );
};

const Tour = ({ steps, isOpen, onRequestClose, onCompleteTour }) => {
  const [run, setRun] = useState(false);
  const [spotlightRect, setSpotlightRect] = useState(null);
  const [instantMove, setInstantMove] = useState(true);
  const [showSkipModal, setShowSkipModal] = useState(false);
  const helpersRef = useRef(null);
  const isFinishingRef = useRef(false);
  const currentStepIndexRef = useRef(0);
  const showSkipModalRef = useRef(false);

  const setSkipModalOpen = (open) => {
    showSkipModalRef.current = open;
    setShowSkipModal(open);
  };

  const finishTour = useCallback(() => {
    if (isFinishingRef.current) {
      return;
    }

    isFinishingRef.current = true;
    currentStepIndexRef.current = 0;
    showSkipModalRef.current = false;
    setRun(false);
    setSpotlightRect(null);
    setShowSkipModal(false);
    document.body.classList.remove("rb-tour-active");
    document.body.classList.remove("rb-tour-skip-open");
    onRequestClose();
    onCompleteTour();
  }, [onCompleteTour, onRequestClose]);

  const updateSpotlight = useCallback((targetSelector, immediate = false) => {
    if (immediate) {
      setInstantMove(true);
    }

    requestAnimationFrame(() => {
      const rect = getSpotlightRect(targetSelector);

      if (!rect) {
        return;
      }

      setSpotlightRect(rect);

      if (immediate) {
        requestAnimationFrame(() => {
          setInstantMove(false);
        });
      }
    });
  }, []);

  useEffect(() => {
    isFinishingRef.current = false;
    currentStepIndexRef.current = 0;
    showSkipModalRef.current = false;
    setShowSkipModal(false);

    if (isOpen && steps.length > 0) {
      setRun(false);
      setSpotlightRect(null);
      setInstantMove(true);

      const timer = window.setTimeout(() => {
        setRun(true);
        document.body.classList.add("rb-tour-active");
      }, 200);

      return () => {
        window.clearTimeout(timer);
        document.body.classList.remove("rb-tour-active");
      };
    }

    setRun(false);
    setSpotlightRect(null);
    document.body.classList.remove("rb-tour-active");
  }, [isOpen, steps]);

  useEffect(() => {
    if (!run) {
      return undefined;
    }

    const refreshSpotlight = () => {
      const target = steps[currentStepIndexRef.current]?.target;
      if (target) {
        updateSpotlight(target, true);
      }
    };

    window.addEventListener("resize", refreshSpotlight);
    window.addEventListener("orientationchange", refreshSpotlight);

    return () => {
      window.removeEventListener("resize", refreshSpotlight);
      window.removeEventListener("orientationchange", refreshSpotlight);
    };
  }, [run, steps, updateSpotlight]);

  const handleJoyrideCallback = (data) => {
    const { action, index, status, step, type } = data;

    if (showSkipModalRef.current) {
      return;
    }

    if (
      type === EVENTS.TARGET_NOT_FOUND ||
      type === "error:target_not_found"
    ) {
      if (index < steps.length - 1) {
        helpersRef.current?.next();
      } else {
        finishTour();
      }
      return;
    }

    if (action === ACTIONS.SKIP || status === STATUS.SKIPPED) {
      finishTour();
      return;
    }

    if (status === STATUS.FINISHED) {
      finishTour();
      return;
    }

    if (typeof index === "number") {
      currentStepIndexRef.current = index;
    }

    if (type === EVENTS.TOUR_START && steps[0]?.target) {
      updateSpotlight(steps[0].target, true);
      return;
    }

    if (type === EVENTS.TOOLTIP && step?.target) {
      updateSpotlight(step.target);
    }
  };

  useEffect(() => {
    if (showSkipModal) {
      document.body.classList.add("rb-tour-skip-open");
    } else {
      document.body.classList.remove("rb-tour-skip-open");
    }

    return () => {
      document.body.classList.remove("rb-tour-skip-open");
    };
  }, [showSkipModal]);

  const renderTooltip = useCallback(
    (tooltipProps) => (
      <TourTooltip
        {...tooltipProps}
        isTourLocked={() => showSkipModalRef.current}
        onRequestSkip={() => setSkipModalOpen(true)}
      />
    ),
    []
  );

  if (!isOpen || steps.length === 0) {
    return null;
  }

  return (
    <>
      {run && <TourOverlay rect={spotlightRect} instant={instantMove} />}

      <Joyride
        steps={steps}
        continuous={true}
        showBackButton={false}
        showProgress={false}
        showSkipButton={false}
        hideCloseButton={true}
        disableOverlay={true}
        disableScrolling={true}
        disableScrollParentFix={true}
        disableCloseOnEsc={true}
        scrollToFirstStep={false}
        spotlightClicks={false}
        run={run}
        tooltipComponent={renderTooltip}
        getHelpers={(helpers) => {
          helpersRef.current = helpers;
        }}
        callback={handleJoyrideCallback}
        floaterProps={{
          hideArrow: false,
          styles: {
            arrow: {
              color: BOX_GREEN,
              length: 16,
              spread: 24,
            },
          },
          options: {
            gpuAcceleration: false,
            preventOverflow: {
              padding: 16,
            },
          },
        }}
        styles={{
          options: {
            zIndex: 1000002,
            arrowColor: BOX_GREEN,
            backgroundColor: "transparent",
          },
          tooltip: {
            backgroundColor: "transparent",
            padding: 0,
            borderRadius: 8,
            width: "auto",
          },
          tooltipContainer: {
            textAlign: "center",
          },
          buttonNext: {
            display: "none",
          },
          buttonBack: {
            display: "none",
          },
          buttonSkip: {
            display: "none",
          },
          buttonClose: {
            display: "none",
          },
        }}
        locale={{
          last: "Done",
          skip: "Skip",
          next: "Next",
        }}
      />

      <SkipOnboardingModal
        isOpen={showSkipModal}
        onContinue={() => setSkipModalOpen(false)}
        onSkip={finishTour}
      />
    </>
  );
};

export default Tour;
